/**
 * Client-side CSV export for admin orders.
 * Swiss-friendly format: semicolon separator + UTF-8 BOM (for Excel FR / CH).
 */
import { ORDER_STATUS_LABELS } from "@/lib/orderStatus";

const escapeCell = (v) => {
    if (v == null) return "";
    const s = String(v);
    // Escape " by doubling; wrap in quotes if contains ; " newline
    if (s.includes(";") || s.includes('"') || s.includes("\n") || s.includes("\r")) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
};

const formatDateCH = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString("fr-CH", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

const formatDateOnlyCH = (iso) => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("fr-CH");
    } catch {
        return iso;
    }
};

const orderRef = (id) => (id || "").slice(0, 8).toUpperCase();

export function ordersToCsv(orders) {
    const headers = [
        "N° commande",
        "Date de commande",
        "Raison sociale",
        "Client",
        "E-mail",
        "Téléphone",
        "Statut",
        "Total HT (CHF)",
        "Nb articles",
        "Livraison souhaitée",
        "Notes",
        "Note globale (/5)",
        "Réclamation",
        "Statut réclamation",
        "Commentaire client",
    ];

    const rows = orders.map((o) => {
        const review = o.review || {};
        const rating = review.rating != null ? String(review.rating) : "";
        const complaint = review.has_complaint ? "Oui" : "";
        const complaintStatus = review.has_complaint
            ? (review.complaint_status === "resolved" ? "Résolue" : "Ouverte")
            : "";
        const comment = review.comment || "";
        return [
            orderRef(o.id),
            formatDateCH(o.created_at),
            o.user_raison_sociale || "",
            `${o.user_prenom || ""} ${o.user_nom || ""}`.trim(),
            o.user_email || "",
            o.user_telephone || "",
            ORDER_STATUS_LABELS[o.status] || o.status || "",
            Number(o.total_ht || 0).toFixed(2),
            String((o.items || []).length),
            formatDateOnlyCH(o.delivery_date),
            (o.notes || "").replace(/\r?\n/g, " · "),
            rating,
            complaint,
            complaintStatus,
            comment.replace(/\r?\n/g, " · "),
        ];
    });

    const csv = [headers, ...rows]
        .map((row) => row.map(escapeCell).join(";"))
        .join("\r\n");

    // UTF-8 BOM (Excel FR needs it to detect encoding)
    return "\uFEFF" + csv;
}

/**
 * Trigger a browser download for the given CSV content.
 */
export function downloadCsv(filename, csvContent) {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, 200);
}

export function csvFilenameForOrders(filter) {
    const today = new Date().toISOString().slice(0, 10);
    const suffix = filter && filter !== "all" ? `-${filter}` : "";
    return `boucherie-costa-commandes${suffix}-${today}.csv`;
}
